import React, { useEffect, useMemo, useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import Popper from "@mui/material/Popper";
import Paper from "@mui/material/Paper";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import MenuList from "@mui/material/MenuList";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { observer } from "mobx-react-lite";
import { ApplicationStore } from "../../../librettoEditor/store/ApplicationStore";
import { CompositionService } from "../../../librettoEditor/services/CompositionService";
import { CompositionStateStore } from "../../../librettoEditor/store/CompositionStateStore";
import { CANVAS_ID } from "../../../librettoEditor/config/config";
import { TextClip } from "../../../engine/clips/text";
import type { CaptionsTextAlign } from "../../../engine/clips/captions/captions.types";
import type { hex } from "../../../engine/types";

type Props = {
    containerRef: React.RefObject<HTMLDivElement>;
};

const toHex = (value: string): hex => {
    const v = String(value ?? "").trim();
    if (v.startsWith("#")) return v as hex;
    return (`#${v}` as hex);
};

const FONT_FAMILIES = [
    "Roboto",
    "Open Sans",
    "Montserrat",
    "Lato",
    "Poppins",
    "Inter",
    "Oswald",
    "Playfair Display",
    "Arial",
    "Helvetica",
];

const FONT_WEIGHTS = [
    { label: "Regular", value: "normal" },
    { label: "Medium", value: "500" },
    { label: "Semi Bold", value: "600" },
    { label: "Bold", value: "bold" },
];

const TextCanvasToolbar: React.FC<Props> = observer(({ containerRef }) => {
    const selectedVisualElement = ApplicationStore.selectedVisualElement;
    const isCropMode = ApplicationStore.isCropMode;
    const selectedScope = ApplicationStore.sceneEditScope;

    const [fontMenuOpen, setFontMenuOpen] = useState(false);
    const [fontAnchor, setFontAnchor] = useState<HTMLElement | null>(null);
    const [weightMenuOpen, setWeightMenuOpen] = useState(false);
    const [weightAnchor, setWeightAnchor] = useState<HTMLElement | null>(null);
    const [moreMenuOpen, setMoreMenuOpen] = useState(false);
    const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);

    const [fontFamily, setFontFamily] = useState("Roboto");
    const [fontWeight, setFontWeight] = useState("bold");
    const [fontStyle, setFontStyle] = useState<"normal" | "italic">("normal");
    const [fontSize, setFontSize] = useState("56");
    const [textColor, setTextColor] = useState("#FFFFFF");
    const [textAlign, setTextAlign] = useState<CaptionsTextAlign>("center");

    const [toolbarPos, setToolbarPos] = useState<{ left: number; top: number } | null>(null);

    const clip = useMemo(() => {
        if (!selectedVisualElement?.clipId) return null;
        return CompositionService.getClipById(selectedVisualElement.clipId) as any;
    }, [selectedVisualElement?.clipId]);

    const isTextClip = Boolean(clip && String(clip.type) === "text");
    const shouldShowToolbar = Boolean(selectedVisualElement && !isCropMode && isTextClip);

    const sceneKey = useMemo(() => {
        if (!clip || String(clip.type) !== "text") return null;
        const spans = CompositionService.clipSpansMultipleScenes(clip);
        if (selectedScope !== "scene" || !spans) return null;

        const scriptClip: any = CompositionService.getScriptLayerClip?.();
        if (!scriptClip) return null;
        const scenes: any[] = Array.isArray(scriptClip.getScenes?.()) ? scriptClip.getScenes() : [];
        if (!scenes.length) return null;

        const idxExplicit = ApplicationStore.selectedSceneIndex;
        if (idxExplicit != null) {
            const scene = scenes.find((s: any) => Number(s?.index) === Number(idxExplicit)) ?? scenes[idxExplicit];
            return scene?.startSplitId ? String(scene.startSplitId) : "__start__";
        }

        const t = Number(CompositionStateStore.currentTime ?? 0);
        const scene =
            scenes.find((s: any) => t >= Number(s?.timelineStartSec ?? 0) && t < Number(s?.timelineEndSec ?? 0)) ??
            scenes.find((s: any) => t < Number(s?.timelineEndSec ?? 0)) ??
            scenes[scenes.length - 1];
        return scene?.startSplitId ? String(scene.startSplitId) : "__start__";
    }, [clip?.id, selectedScope, ApplicationStore.selectedSceneIndex, CompositionStateStore.currentTime]);

    // Sync UI state from clip when selection/scope changes.
    useEffect(() => {
        if (!clip || String(clip.type) !== "text") return;
        const overrides = (clip as any).sceneStyleOverrides ?? {};
        const ov = sceneKey ? overrides[sceneKey] : undefined;
        const baseStyle = (clip as any).textStyle ?? {};
        const effStyle = { ...baseStyle, ...(ov?.textStyle ?? {}) };
        const effAlign: CaptionsTextAlign = (ov?.textAlign ?? (clip as any).textAlign ?? "center") as CaptionsTextAlign;

        setFontFamily(String(effStyle.fontFamily ?? "Roboto"));
        setFontWeight(String(effStyle.fontWeight ?? "normal"));
        setFontStyle(String(effStyle.fontStyle ?? "normal") === "italic" ? "italic" : "normal");
        setFontSize(String(Number(effStyle.fontSize ?? 56)));
        setTextColor(String(effStyle.fillColor ?? "#FFFFFF"));
        setTextAlign(effAlign === "left" || effAlign === "right" ? effAlign : "center");
    }, [clip?.id, selectedScope, sceneKey]);

    // Position toolbar above the selected text element.
    useEffect(() => {
        const compute = () => {
            if (!clip || String(clip.type) !== "text") return;
            const canvas = document.getElementById(CANVAS_ID) as HTMLCanvasElement | null;
            const comp = CompositionService.composition as any;
            if (!canvas || !comp) return;

            const bounds = typeof (clip as any).view?.getBounds === "function" ? (clip as any).view.getBounds() : null;
            if (!bounds) return;

            const canvasRect = canvas.getBoundingClientRect();
            const compW = Number(comp.settings?.width ?? comp.width ?? 1920);
            const compH = Number(comp.settings?.height ?? comp.height ?? 1080);
            if (!(compW > 0 && compH > 0)) return;

            const scaleX = canvasRect.width / compW;
            const scaleY = canvasRect.height / compH;

            const centerX = canvasRect.left + (Number(bounds.x ?? 0) + Number(bounds.width ?? 0) / 2) * scaleX;
            const topY = canvasRect.top + Number(bounds.y ?? 0) * scaleY;

            setToolbarPos({ left: centerX, top: Math.max(8, topY) });
        };

        compute();
        const onUpdateHandlers = () => compute();
        window.addEventListener("updateHandlers", onUpdateHandlers as any);
        window.addEventListener("resize", onUpdateHandlers as any);
        return () => {
            window.removeEventListener("updateHandlers", onUpdateHandlers as any);
            window.removeEventListener("resize", onUpdateHandlers as any);
        };
    }, [clip?.id, CompositionStateStore.currentTime, selectedScope, sceneKey]);

    if (!shouldShowToolbar || !toolbarPos) return null;

    const targetSceneKey = sceneKey;

    const apply = (fn: (c: TextClip) => void) => {
        if (!clip || String(clip.type) !== "text") return;
        try {
            fn(clip as TextClip);
        } catch {}
        try { CompositionService.recomputeFrame(); } catch {}
        try { window.dispatchEvent(new CustomEvent("updateHandlers")); } catch {}
        try { CompositionService.notifyClipUpdated(clip as any); } catch {}
    };

    const toolbarStyle: React.CSSProperties = {
        position: "fixed",
        left: toolbarPos.left,
        top: toolbarPos.top,
        transform: "translate(-50%, -100%) translateY(-10px)",
        zIndex: 30,
        display: "flex",
        alignItems: "center",
        gap: 4,
        background: "#ffffff",
        border: "1px solid #E5E7EB",
        borderRadius: 10,
        padding: "6px 8px",
        boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
        pointerEvents: "auto",
    };

    const btnStyle: React.CSSProperties = {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: 28,
        padding: "0 10px",
        borderRadius: 6,
        border: "none",
        background: "transparent",
        cursor: "pointer",
        userSelect: "none",
        fontSize: 13,
        color: "#374151",
        transition: "background-color 0.15s",
    };

    const iconBtnStyle: React.CSSProperties = {
        ...btnStyle,
        padding: "0 6px",
        minWidth: 28,
    };

    const activeBtnStyle: React.CSSProperties = {
        ...iconBtnStyle,
        background: "#F3F4F6",
    };

    const dividerStyle: React.CSSProperties = {
        width: 1,
        height: 20,
        background: "#E5E7EB",
        margin: "0 4px",
    };

    return (
        <div
            data-canvas-interactive="true"
            style={toolbarStyle}
            onMouseDown={(e) => {
                e.stopPropagation();
            }}
        >
            {/* Font family */}
            <div
                style={{ ...btnStyle, fontFamily, minWidth: 70 }}
                onClick={(e) => {
                    setFontAnchor(e.currentTarget as HTMLElement);
                    setFontMenuOpen((o) => !o);
                }}
            >
                {fontFamily}
            </div>
            <Popper open={fontMenuOpen} anchorEl={fontAnchor} placement="bottom-start" sx={{ zIndex: 9999 }}>
                <ClickAwayListener onClickAway={() => setFontMenuOpen(false)}>
                    <Paper elevation={3} sx={{ p: 0.5, borderRadius: "10px", maxHeight: 280, overflow: "auto" }}>
                        <MenuList dense>
                            {FONT_FAMILIES.map((f) => (
                                <MenuItem
                                    key={f}
                                    onClick={() => {
                                        setFontFamily(f);
                                        setFontMenuOpen(false);
                                        apply((c) => c.updateTextStyleForScene(targetSceneKey, { fontFamily: f }));
                                    }}
                                >
                                    <span style={{ fontFamily: f }}>{f}</span>
                                </MenuItem>
                            ))}
                        </MenuList>
                    </Paper>
                </ClickAwayListener>
            </Popper>

            {/* Bold/Italic */}
            <Tooltip title="Bold" placement="top">
                <div
                    style={fontWeight === "bold" || fontWeight === "600" ? activeBtnStyle : iconBtnStyle}
                    onClick={() => {
                        const next = fontWeight === "bold" ? "normal" : "bold";
                        setFontWeight(next);
                        apply((c) => c.updateTextStyleForScene(targetSceneKey, { fontWeight: next }));
                    }}
                >
                    <FormatBoldIcon sx={{ fontSize: 18 }} />
                </div>
            </Tooltip>
            <Tooltip title="Italic" placement="top">
                <div
                    style={fontStyle === "italic" ? activeBtnStyle : iconBtnStyle}
                    onClick={() => {
                        const next = fontStyle === "italic" ? "normal" : "italic";
                        setFontStyle(next);
                        apply((c) => c.updateTextStyleForScene(targetSceneKey, { fontStyle: next }));
                    }}
                >
                    <FormatItalicIcon sx={{ fontSize: 18 }} />
                </div>
            </Tooltip>

            {/* Weight dropdown */}
            <div
                style={{ ...btnStyle, minWidth: 82 }}
                onClick={(e) => {
                    setWeightAnchor(e.currentTarget as HTMLElement);
                    setWeightMenuOpen((o) => !o);
                }}
            >
                {FONT_WEIGHTS.find((w) => w.value === fontWeight)?.label ?? "Weight"}
            </div>
            <Popper open={weightMenuOpen} anchorEl={weightAnchor} placement="bottom-start" sx={{ zIndex: 9999 }}>
                <ClickAwayListener onClickAway={() => setWeightMenuOpen(false)}>
                    <Paper elevation={3} sx={{ p: 0.5, borderRadius: "10px" }}>
                        <MenuList dense>
                            {FONT_WEIGHTS.map((w) => (
                                <MenuItem
                                    key={w.value}
                                    onClick={() => {
                                        setFontWeight(w.value);
                                        setWeightMenuOpen(false);
                                        apply((c) => c.updateTextStyleForScene(targetSceneKey, { fontWeight: w.value }));
                                    }}
                                >
                                    {w.label}
                                </MenuItem>
                            ))}
                        </MenuList>
                    </Paper>
                </ClickAwayListener>
            </Popper>

            {/* Font size */}
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <TextField
                    size="small"
                    value={fontSize}
                    onChange={(e) => setFontSize(e.target.value.replace(/[^0-9.]/g, ""))}
                    onBlur={() => {
                        const n = Math.max(8, Math.min(300, Number(fontSize) || 56));
                        setFontSize(String(n));
                        apply((c) => c.updateTextStyleForScene(targetSceneKey, { fontSize: n }));
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            const n = Math.max(8, Math.min(300, Number(fontSize) || 56));
                            setFontSize(String(n));
                            apply((c) => c.updateTextStyleForScene(targetSceneKey, { fontSize: n }));
                            (e.target as HTMLInputElement).blur();
                        }
                    }}
                    sx={{ "& .MuiInputBase-input": { py: 0.5, fontSize: 12, width: 36, textAlign: "right" } }}
                />
            </div>

            {/* Color */}
            <input
                type="color"
                value={textColor}
                onChange={(e) => {
                    const v = toHex(e.target.value);
                    setTextColor(v);
                    apply((c) => c.updateTextStyleForScene(targetSceneKey, { fillColor: v }));
                }}
                style={{
                    width: 24,
                    height: 24,
                    borderRadius: 6,
                    border: "1px solid #E5E7EB",
                    background: "transparent",
                    cursor: "pointer",
                }}
            />

            <div style={dividerStyle} />

            {/* Align */}
            <Tooltip title="Align left" placement="top">
                <div
                    style={textAlign === "left" ? activeBtnStyle : iconBtnStyle}
                    onClick={() => {
                        setTextAlign("left");
                        apply((c) => c.updateTextAlignForScene(targetSceneKey, "left"));
                    }}
                >
                    <FormatAlignLeftIcon sx={{ fontSize: 18 }} />
                </div>
            </Tooltip>
            <Tooltip title="Align center" placement="top">
                <div
                    style={textAlign === "center" ? activeBtnStyle : iconBtnStyle}
                    onClick={() => {
                        setTextAlign("center");
                        apply((c) => c.updateTextAlignForScene(targetSceneKey, "center"));
                    }}
                >
                    <FormatAlignCenterIcon sx={{ fontSize: 18 }} />
                </div>
            </Tooltip>
            <Tooltip title="Align right" placement="top">
                <div
                    style={textAlign === "right" ? activeBtnStyle : iconBtnStyle}
                    onClick={() => {
                        setTextAlign("right");
                        apply((c) => c.updateTextAlignForScene(targetSceneKey, "right"));
                    }}
                >
                    <FormatAlignRightIcon sx={{ fontSize: 18 }} />
                </div>
            </Tooltip>

            <div style={dividerStyle} />

            {/* More menu */}
            <div
                style={iconBtnStyle}
                onClick={(e) => {
                    setMoreAnchor(e.currentTarget as HTMLElement);
                    setMoreMenuOpen((o) => !o);
                }}
            >
                <MoreHorizIcon sx={{ fontSize: 18 }} />
            </div>
            <Popper open={moreMenuOpen} anchorEl={moreAnchor} placement="bottom-end" sx={{ zIndex: 9999 }}>
                <ClickAwayListener onClickAway={() => setMoreMenuOpen(false)}>
                    <Paper elevation={3} sx={{ p: 0.5, borderRadius: "10px", minWidth: 160 }}>
                        <MenuList dense>
                            <MenuItem
                                onClick={() => {
                                    try { ApplicationStore.startEditingTextClip(String(clip?.id ?? "")); } catch {}
                                    setMoreMenuOpen(false);
                                }}
                            >
                                Edit text
                            </MenuItem>
                            <Divider />
                            <MenuItem
                                onClick={() => {
                                    try { CompositionService.deleteClipById(String(clip?.id ?? "")); } catch {}
                                    setMoreMenuOpen(false);
                                }}
                                sx={{ color: "#DC2626" }}
                            >
                                Delete
                            </MenuItem>
                        </MenuList>
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </div>
    );
});

export default TextCanvasToolbar;
